import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthenticatedUser } from '../models/auth/authenticated-user.model';
import { SecuredSessionEncodedData } from '../models/auth/secured-session-encoded-data.model';
import { UserCompany } from '../models/users/user-company.model';
import { LocalStorageService } from '../services/local-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<AuthenticatedUser>;
    private currentTertSubject: BehaviorSubject<UserCompany>;
    public currentUser: Observable<AuthenticatedUser>;
    public currentTert: Observable<UserCompany>;

    private privilegesSubject: BehaviorSubject<boolean>;
    public privilegesObservable: Observable<any>;

    public isAuthenticated = false;
    public isQrSignatureRoute = false;
    public isFunctionalUser = false;
    public selectedTert!: UserCompany;

    constructor(
        private http: HttpClient,
        private localStorageService: LocalStorageService
    ) {
        this.currentUserSubject = new BehaviorSubject<AuthenticatedUser>(JSON.parse(localStorageService.retrieveCurrentUser()));
        this.currentUser = this.currentUserSubject.asObservable();

        this.currentTertSubject = new BehaviorSubject<UserCompany>(null);
        this.currentTert = this.currentTertSubject.asObservable();

        this.privilegesSubject = new BehaviorSubject<boolean>(false);
        this.privilegesObservable = this.privilegesSubject.asObservable();
    }

    public get currentUserValue(): AuthenticatedUser {
        if (this.localStorageService.retrieveCurrentUser()) {
            return this.currentUserSubject.value;
        }

        return null;
    }

    private setCurrentUserValue(user: AuthenticatedUser) {
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        this.isFunctionalUser = user.userResource.isFunctionalUser;
        this.localStorageService.storeCurrentUser(JSON.stringify(user), user.userResource.id);
        this.currentUserSubject.next(user);
    }

    public getSessionEncodedData(ssoId: string): Observable<SecuredSessionEncodedData> {
        const apiUrl = environment.apiUrl + '/api/sso';

        return this.http.get<SecuredSessionEncodedData>(`${apiUrl}/${ssoId}`);
    }

    public login(username: string, password: string, code: string, lang: string, token: string) {
        return this.http
            .post<any>(`${environment.usersUrl}/auth/login`, {
                username,
                password,
                code,
                lang,
                token
            })
            .pipe(
                map((user) => {
                    return user;
                })
            );
    }

    public populateUserCompanies(user) {
        return this.http
            .get<any>(`${environment.coreUrl}/api/v1/user-companies/populate-user`, {
                headers: { Authorization: `Bearer ${user.accessToken}` }
            })
            .pipe(
                map((userResource) => {
                    user.userResource = userResource;
                    this.setLoggedInUser(user);

                    return user;
                })
            );
    }

    public setLoggedInUser(user: AuthenticatedUser) {
        this.setCurrentUserValue(user);
        this.isAuthenticated = true;
        this.privilegesSubject.next(true);
    }

    public logout() {
        // remove user from local storage to log user out
        this.localStorageService.removeCurrentUser();
        this.currentUserSubject.next(null);
        this.isAuthenticated = false;
    }

    public setCurrentTert(tert: UserCompany) {
        this.selectedTert = tert;

        this.currentTertSubject.next(tert);
    }

    public isTokenValid() {
        const user = this.currentUserValue;
        const currentTime = new Date().getTime();
        if (user && user.accessToken && user.expiresIn && currentTime <= user.expiresIn) {
            return true;
        }

        return false;
    }
}
